/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

interface ImageNavArrowsProps {
    imageCount: number;
    onIndexChange: React.Dispatch<React.SetStateAction<number>>;
    /** Arrow button size: "sm" for PLP/cart, "lg" for PDP */
    size?: 'sm' | 'lg';
    className?: string;
    variant?: 'default' | 'circular';
}

export default function ImageNavArrows({
    imageCount,
    onIndexChange,
    size = 'sm',
    className,
    variant = 'default',
}: ImageNavArrowsProps) {
    const { t } = useTranslation('common');

    const goPrev = () => onIndexChange((i) => (i <= 0 ? imageCount - 1 : i - 1));
    const goNext = () => onIndexChange((i) => (i >= imageCount - 1 ? 0 : i + 1));

    const isLarge = size === 'lg';
    const isCircular = variant === 'circular';

    return (
        <>
            <button
                type="button"
                onClick={goPrev}
                className={cn(
                    'absolute -translate-y-1/2 cursor-pointer transition-all prev-btn',
                    isLarge ? 'top-[calc(50%-0.5px)]' : 'top-1/2',
                    isCircular
                        ? cn(
                            'rounded-full bg-white text-black hover:bg-gray-50 flex items-center justify-center',
                            isLarge
                                ? 'left-[6px] md:left-3 h-[30px] w-[30px] md:h-[42px] md:w-[42px] border border-white shadow-none z-[3]'
                                : 'left-4 h-10 w-10 border border-gray-100 shadow-md'
                        )
                        : cn(
                            'rounded-none bg-background/90 hover:bg-background',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                            isLarge ? 'left-4 p-3 shadow-lg' : 'left-2 p-1.5 shadow-md'
                        ),
                    className
                )}
                aria-label={t('previousImage')}>
                {isCircular ? (
                    <ArrowLeft
                        className={isLarge ? 'size-4 md:h-6 md:w-[30px]' : 'size-5'}
                        strokeWidth={isLarge ? 1 : 2}
                    />
                ) : (
                    <ChevronLeft className={isLarge ? 'size-6' : 'size-4'} />
                )}
            </button>
            <button
                type="button"
                onClick={goNext}
                className={cn(
                    'absolute -translate-y-1/2 cursor-pointer transition-all next-btn',
                    isLarge ? 'top-[calc(50%-0.5px)]' : 'top-1/2',
                    isCircular
                        ? cn(
                            'rounded-full bg-white text-black hover:bg-gray-50 flex items-center justify-center',
                            isLarge
                                ? 'right-[6px] md:right-3 h-[30px] w-[30px] md:h-[42px] md:w-[42px] border border-white shadow-none z-[2]'
                                : 'right-4 h-10 w-10 border border-gray-100 shadow-md'
                        )
                        : cn(
                            'rounded-none bg-background/90 hover:bg-background',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                            isLarge ? 'right-4 p-3 shadow-lg' : 'right-2 p-1.5 shadow-md'
                        ),
                    className
                )}
                aria-label={t('nextImage')}>
                {isCircular ? (
                    <ArrowRight
                        className={isLarge ? 'size-4 md:h-6 md:w-[30px]' : 'size-5'}
                        strokeWidth={isLarge ? 1 : 2}
                    />
                ) : (
                    <ChevronRight className={isLarge ? 'size-6' : 'size-4'} />
                )}
            </button>
        </>
    );
}
